import React, { useState, useCallback } from 'react';
import { Mail, MapPin, Send, MessageSquare } from 'lucide-react';
import { brand } from '../mock';
import { useToast } from '../hooks/use-toast';

// Named constants (avoid magic numbers)
const SUBMIT_FEEDBACK_DELAY_MS = 700;
const INITIAL_FORM_STATE = Object.freeze({ name: '', email: '', subject: '', message: '' });

function ContactHero() {
  return (
    <section className="bg-brand-green text-brand-cream py-20 md:py-24">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
        <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Contact</span>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-tight">
          Let&apos;s talk.
        </h1>
        <p className="mt-6 text-lg text-brand-cream/80 max-w-2xl mx-auto">
          Questions, feedback, or ready to close more deals with AI? We&apos;d love to hear from you.
        </p>
      </div>
    </section>
  );
}

function ContactInfo() {
  return (
    <div className="lg:col-span-2">
      <h2 className="font-serif text-3xl text-brand-green">Get in touch</h2>
      <p className="mt-4 text-brand-green/70 leading-relaxed">
        We usually respond within 1 business day. For anything urgent, email us directly.
      </p>

      <div className="mt-10 space-y-6">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-brand-green flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5 text-brand-cream" />
          </div>
          <div>
            <div className="text-sm text-brand-green/60">Email</div>
            <a href={`mailto:${brand.email}`} className="font-serif text-lg text-brand-green hover:text-brand-green/70">
              {brand.email}
            </a>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-brand-green flex items-center justify-center shrink-0">
            <MapPin className="w-5 h-5 text-brand-cream" />
          </div>
          <div>
            <div className="text-sm text-brand-green/60">Office</div>
            <div className="font-serif text-lg text-brand-green">{brand.address}</div>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-brand-green flex items-center justify-center shrink-0">
            <MessageSquare className="w-5 h-5 text-brand-cream" />
          </div>
          <div>
            <div className="text-sm text-brand-green/60">Support hours</div>
            <div className="font-serif text-lg text-brand-green">Mon–Fri, 9am–6pm PT</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactForm({ form, submitting, onChange, onSubmit }) {
  return (
    <div className="lg:col-span-3">
      <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-brand-cream-dark p-8 md:p-10 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="name" className="block text-sm text-brand-green/70 mb-2">Name</label>
            <input
              id="name" name="name" required value={form.name} onChange={onChange}
              className="w-full px-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green/40 transition-colors"
              placeholder="Your name"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm text-brand-green/70 mb-2">Email</label>
            <input
              id="email" name="email" type="email" required value={form.email} onChange={onChange}
              className="w-full px-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green/40 transition-colors"
              placeholder="you@brokerage.com"
            />
          </div>
        </div>
        <div>
          <label htmlFor="subject" className="block text-sm text-brand-green/70 mb-2">Subject</label>
          <input
            id="subject" name="subject" required value={form.subject} onChange={onChange}
            className="w-full px-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green/40 transition-colors"
            placeholder="How can we help?"
          />
        </div>
        <div>
          <label htmlFor="message" className="block text-sm text-brand-green/70 mb-2">Message</label>
          <textarea
            id="message" name="message" required rows="6" value={form.message} onChange={onChange}
            className="w-full px-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green/40 transition-colors resize-none"
            placeholder="Tell us a bit about your team, listings, or what you&apos;re looking for..."
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-brand-green text-brand-cream font-semibold hover:scale-[1.02] transition-transform disabled:opacity-70 disabled:hover:scale-100"
        >
          {submitting ? 'Sending…' : (<>Send message <Send className="w-4 h-4" /></>)}
        </button>
      </form>
    </div>
  );
}

function persistMessage() {
  // Frontend-only demo: intentionally not persisting user submissions.
  // Sensitive form data (name, email, message) should NOT be stored in
  // localStorage where it is accessible to any script (XSS risk).
  // A real backend endpoint should be used when server-side handling is added.
}

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [submitting, setSubmitting] = useState(false);

  // setState setters (setForm) are stable per React docs, so an empty
  // dependency array is correct here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setSubmitting(true);
    persistMessage(form);
    setTimeout(() => {
      setSubmitting(false);
      setForm(INITIAL_FORM_STATE);
      toast({
        title: 'Message received',
        description: 'Thanks for reaching out. We will get back to you within 1 business day.',
      });
    }, SUBMIT_FEEDBACK_DELAY_MS);
  }, [form, toast]);

  return (
    <div className="bg-brand-cream">
      <ContactHero />
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <ContactInfo />
          <ContactForm form={form} submitting={submitting} onChange={handleChange} onSubmit={handleSubmit} />
        </div>
      </section>
    </div>
  );
}
